import React from 'react';
import {
    Skeleton,
    styled,
    Tooltip,
    Typography,
} from '@mui/material';

import { ApiStatus, ValueReferences } from '../../utils/enums';
import { formatBalance } from '../../utils/formattingUtils';

const classesPrefix = 'balanceLabel';

const classes = {
    goodBalance: `${classesPrefix}-goodBalance`,
    okayBalance: `${classesPrefix}-okayBalance`,
    poorBalance: `${classesPrefix}-poorBalance`,
    dailyBalanceLabel: `${classesPrefix}-dailyBalanceLabel`,
};

const StyledTypography = styled(Typography)(({ theme }) => {
    return {
        [`& .${classes.goodBalance}`]: {
            color: theme.palette.success.main,
        },
        [`& .${classes.okayBalance}`]: {
            color: theme.palette.warning.dark,
        },
        [`& .${classes.poorBalance}`]: {
            color: theme.palette.error.main,
        },
        [`& .${classes.dailyBalanceLabel}`]: {
            color: theme.palette.text.secondary,
            fontSize: theme.typography.h6.fontSize,
        },
    };
});

const BalanceLabel = ({
    apiStatus,
    balanceType,
    isDailyBalance,
    balanceAmount,
    unitLabel = '',
}: {
    apiStatus: ApiStatus;
    balanceType: 'area' | 'lane';
    isDailyBalance: boolean;
    balanceAmount: number | null;
    unitLabel?: string;
}): React.JSX.Element => {
    if (apiStatus === ApiStatus.Idle || apiStatus === ApiStatus.Loading) {
        return (
            <Typography variant='h3' gutterBottom>
                <Skeleton width={100} />
            </Typography>
        );
    }

    if (apiStatus === ApiStatus.Failure || balanceAmount === null) {
        return (
            <Typography variant='h3' gutterBottom>
                {ValueReferences.NA}
            </Typography>
        );
    }

    let tooltipTitle = '';
    let className: string;

    const absoluteBalance = Math.abs(balanceAmount);
    const goodThreshold = isDailyBalance ? 2 : 10;
    const okayThreshold = isDailyBalance ? 5 : 20;

    if (absoluteBalance === 0) {
        tooltipTitle = `This ${balanceType} is perfectly balanced`;
        className = classes.goodBalance;
    } else if (absoluteBalance <= goodThreshold) {
        tooltipTitle = `This ${balanceType} is well-balanced.`;
        className = classes.goodBalance;
    } else if (absoluteBalance <= okayThreshold) {
        tooltipTitle = `This ${balanceType} is imbalanced.`;
        className = classes.okayBalance;
    } else {
        tooltipTitle = `This ${balanceType} is severely imbalanced.`;
        className = classes.poorBalance;
    }

    return (
        <Tooltip title={tooltipTitle}>
            <StyledTypography variant='h3' gutterBottom>
                <span className={className}>{formatBalance(balanceAmount)}</span>
                {
                    unitLabel &&
                    <span className={classes.dailyBalanceLabel}>{unitLabel}</span>
                }
            </StyledTypography>
        </Tooltip>
    );
};

export default BalanceLabel;
